import { useState } from 'react';
import type { DeckCard } from '@sfc/sdk';
import { compensationLabel, postedLabel, stageLabel } from '../vocabulary';

/**
 * One idea, as a stranger sees it.
 *
 * There is nowhere on this card for a name, because the response has no field
 * for one. What a reader gets is the work: what it is, how far along, what it
 * is looking for and what it pays in.
 */
export function Card({
  card,
  offset,
  leaving,
  dragging,
  handlers,
}: {
  card: DeckCard;
  offset: number;
  leaving?: 'right' | 'left';
  dragging: boolean;
  handlers: React.HTMLAttributes<HTMLElement>;
}): React.JSX.Element {
  /*
   * A pitch may run to twelve hundred characters, and a card has a size.
   * Clamped to what somebody reads before deciding, with the rest one tap
   * away -- rather than overflowing, which cuts a sentence mid-word and reads
   * as a broken card rather than a long one.
   */
  const [expanded, setExpanded] = useState(false);
  const long = card.pitch.length > 260;

  const tilt = offset / 22;
  const verdict = leaving ?? (Math.abs(offset) > 56 ? (offset > 0 ? 'right' : 'left') : undefined);
  // The seam takes the colour of the side being chosen well before the
  // threshold, so the card answers the gesture while it is still happening.
  const toward = leaving ?? (Math.abs(offset) > 18 ? (offset > 0 ? 'right' : 'left') : undefined);

  return (
    <article
      className={[
        'card',
        'card--front',
        dragging ? 'card--dragging' : '',
        toward ? `card--toward-${toward}` : '',
        leaving ? `card--leaving card--leaving-${leaving}` : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        leaving
          ? undefined
          : {
              transform: offset ? `translateX(${offset}px) rotate(${tilt}deg)` : undefined,
              // Below the card, so it swings like a sheet pushed across a
              // desk rather than spinning about its middle.
              transformOrigin: '50% 140%',
            }
      }
      data-testid="card"
      {...handlers}
    >
      {/* Mirrors the gesture back while it is happening, so nobody has to let
          go to find out which way they were going. */}
      <span className={`verdict verdict--${verdict ?? 'none'}`} aria-hidden="true">
        {verdict === 'right' ? 'Interested' : 'Pass'}
      </span>

      <header className="card__head">
        <span className="chip chip--stage">{stageLabel(card.stage)}</span>
        <span className="chip">{compensationLabel(card.compensationType)}</span>
      </header>

      <h2 className="card__title" data-testid="card-title">
        {card.title}
      </h2>
      <p className={`card__pitch${long && !expanded ? ' card__pitch--clamped' : ''}`}>
        {card.pitch}
      </p>
      {long && !expanded && (
        <button
          className="card__more"
          type="button"
          data-testid="read-all"
          /* The card is a drag surface; a press inside it must not start one. */
          onPointerDown={(event) => event.stopPropagation()}
          onClick={() => setExpanded(true)}
        >
          Read it all
        </button>
      )}

      {/* Every idea is two halves: the work, and who it is missing. The line
          is where they meet, and it is the only structural device here. */}
      <div className="card__seam" aria-hidden="true" />

      <div className="card__looking">
        <h3 className="card__label">Looking for</h3>
        <ul className="skills">
          {card.skills.map((skill) => (
            <li key={skill.id} className="chip chip--skill">
              {skill.label}
            </li>
          ))}
        </ul>
      </div>

      <footer className="card__foot">
        <span>{postedLabel(card.postedAt)}</span>
        {card.matchingSkills > 0 && (
          <span className="card__overlap" data-testid="card-overlap">
            {card.matchingSkills === 1
              ? 'Asks for 1 of your skills'
              : `Asks for ${card.matchingSkills} of your skills`}
          </span>
        )}
      </footer>
    </article>
  );
}
